/**
 * React Hook Form / Zod Resolver
 *
 * Utility for integrating Zod with React Hook Form.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { type FieldErrors, type FieldValues, type Resolver } from 'react-hook-form';
import { z } from 'zod';

/**
 * Creates a react-hook-form resolver from a Zod schema.
 * Uses zodResolver from @hookform/resolvers v5 which supports Zod v4 natively.
 *
 * The cast to Resolver<T> collapses the Zod v4 input/output distinction so form
 * components can continue using z.infer (output type) for both TFieldValues and
 * the handleSubmit callback — consistent with how Zod v3 behaved.
 *
 * @example
 * ```tsx
 * const schema = z.object({
 *   name: z.string().min(1, 'Name is required'),
 *   email: z.string().email('Invalid email')
 * });
 *
 * const { register, handleSubmit, formState: { errors } } = useForm({
 *   resolver: createZodResolver(schema)
 * });
 * ```
 */
export function createZodResolver<T extends FieldValues>(schema: z.ZodType<T>): Resolver<T> {
  const anyResolver = zodResolver as (s: unknown) => unknown;
  const resolver = anyResolver(schema) as Resolver<T>;

  // @hookform/resolvers/zod detects ZodErrors via Array.isArray(e?.errors), which works
  // for Zod v3 (.errors) but fails for Zod v4 (.issues). When detection fails the resolver
  // re-throws the ZodError as an unhandled rejection. Wrap to catch and convert it.
  return async (values, context, options) => {
    try {
      return await resolver(values, context, options);
    } catch (error: unknown) {
      if (
        error !== null &&
        typeof error === 'object' &&
        'issues' in error &&
        Array.isArray((error as { issues: unknown[] }).issues)
      ) {
        const zodError = error as z.ZodError;
        const errors: Record<string, { message: string; type: string }> = {};
        for (const issue of zodError.issues) {
          const path = issue.path.join('.');
          // Guard against prototype-polluting keys (e.g. __proto__, constructor)
          if (path === '__proto__' || path === 'constructor' || path === 'prototype') continue;
          if (path && !Object.hasOwn(errors, path)) {
            // eslint-disable-next-line security/detect-object-injection
            errors[path] = { message: issue.message, type: String(issue.code) };
          }
        }
        return { values: {} as never, errors: errors as FieldErrors<T> };
      }
      throw error;
    }
  };
}
